'use strict'

import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import {
  parseYmd, toYmd, addDays, getMonday,
  getWeekTypeForDate, getDayName, WORK_DAYS
} from './scheduleUtils'

const DAILY_OT_THRESHOLD_MIN = 480

/**
 * Manages focused-route fetching, context stats, and weekly crew stats.
 *
 * @param {{ url: string, selectedScenarioId: string, focusCrewId: string, focusDate: string, planningMode: string, contextWeekType: string }} config
 */
export default function useRouteContext ({ url, selectedScenarioId, focusCrewId, focusDate, planningMode, contextWeekType }) {
  const [focusedRoute, setFocusedRoute] = useState(null)
  const [loadingFocusedRoute, setLoadingFocusedRoute] = useState(false)
  const [focusContextStats, setFocusContextStats] = useState(null)
  const [loadingFocusContextStats, setLoadingFocusContextStats] = useState(false)
  const [weeklyCrewStats, setWeeklyCrewStats] = useState(null)

  // ── Focused route ───────────────────────────────────────────

  const fetchFocusedRoute = useCallback(async () => {
    if (!focusCrewId || !focusDate) {
      setFocusedRoute(null)
      return
    }
    setLoadingFocusedRoute(true)
    try {
      const routeUrl = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${focusCrewId}/route`
        : `${url}/api/hmi/territory/crews/${focusCrewId}/route`
      const response = await axios.get(routeUrl, { params: { date: focusDate } })
      setFocusedRoute(response.data || null)
    } catch (_err) {
      setFocusedRoute(null)
    } finally {
      setLoadingFocusedRoute(false)
    }
  }, [focusCrewId, focusDate, selectedScenarioId, url])

  useEffect(() => {
    fetchFocusedRoute()
  }, [fetchFocusedRoute])

  // ── Context dates ───────────────────────────────────────────

  const contextDates = useMemo(() => {
    const anchorDate = parseYmd(focusDate)
    if (planningMode === 'day') {
      return [anchorDate]
    }

    const weekStart = getMonday(anchorDate)
    const weekDates = WORK_DAYS.map((_, idx) => addDays(weekStart, idx))

    if (planningMode === 'week') {
      return weekDates
    }

    const nextWeekStart = addDays(weekStart, 7)
    const nextWeekDates = WORK_DAYS.map((_, idx) => addDays(nextWeekStart, idx))
    return [...weekDates, ...nextWeekDates]
  }, [focusDate, planningMode])

  const filteredContextDates = useMemo(() => {
    if (contextWeekType === 'ALL') return contextDates
    return contextDates.filter(date => getWeekTypeForDate(date) === contextWeekType)
  }, [contextDates, contextWeekType])

  // ── Focus context stats ─────────────────────────────────────

  const fetchFocusContextStats = useCallback(async () => {
    if (!focusCrewId || !filteredContextDates.length) {
      setFocusContextStats(null)
      return
    }

    setLoadingFocusContextStats(true)
    try {
      const routeBase = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${focusCrewId}/route`
        : `${url}/api/hmi/territory/crews/${focusCrewId}/route`

      const responses = await Promise.all(
        filteredContextDates.map(date =>
          axios.get(routeBase, { params: { date: toYmd(date) } })
            .catch(() => ({ data: null }))
        )
      )

      const days = responses.map((res, idx) => {
        const data = res.data || {}
        const stops = data.stops || []
        const date = toYmd(filteredContextDates[idx])
        const serviceMin = stops.reduce((sum, s) => sum + Math.round(Number(s.serviceMinutes || 0)), 0)
        const driveMin = stops.reduce((sum, s) => sum + Math.round(Number(s.driveTimeMinutes || 0)), 0)
        const totalMin = serviceMin + driveMin
        const otMin = Math.max(0, totalMin - DAILY_OT_THRESHOLD_MIN)
        return {
          date,
          dayName: getDayName(parseYmd(date)),
          weekType: getWeekTypeForDate(parseYmd(date)),
          stops: stops.length,
          serviceMin,
          driveMin,
          totalMin,
          otMin,
          mode: data.meta?.mode || 'live'
        }
      })

      const summary = days.reduce((acc, day) => {
        acc.stops += day.stops
        acc.serviceMin += day.serviceMin
        acc.driveMin += day.driveMin
        acc.totalMin += day.totalMin
        acc.otMin += day.otMin
        return acc
      }, {
        stops: 0,
        serviceMin: 0,
        driveMin: 0,
        totalMin: 0,
        otMin: 0
      })

      setFocusContextStats({ days, summary })
    } catch (_err) {
      setFocusContextStats(null)
    } finally {
      setLoadingFocusContextStats(false)
    }
  }, [filteredContextDates, focusCrewId, selectedScenarioId, url])

  useEffect(() => {
    fetchFocusContextStats()
  }, [fetchFocusContextStats])

  // ── Weekly crew stats ───────────────────────────────────────

  const fetchWeeklyCrewStats = useCallback(async () => {
    if (!focusCrewId) {
      setWeeklyCrewStats(null)
      return
    }
    const weekStart = getMonday(parseYmd(focusDate))
    const weekDates = WORK_DAYS.map((_, idx) => addDays(weekStart, idx))

    try {
      const routeBase = selectedScenarioId
        ? `${url}/api/hmi/territory/scenarios/${selectedScenarioId}/crews/${focusCrewId}/route`
        : `${url}/api/hmi/territory/crews/${focusCrewId}/route`

      const responses = await Promise.all(
        weekDates.map(date =>
          axios.get(routeBase, { params: { date: toYmd(date) } })
            .catch(() => ({ data: null }))
        )
      )

      const days = responses.map((res, idx) => {
        const data = res.data || {}
        const stops = data.stops || []
        const date = toYmd(weekDates[idx])
        const serviceMin = stops.reduce((sum, s) => sum + Math.round(Number(s.serviceMinutes || 0)), 0)
        const actualServiceMin = stops.reduce((sum, s) => {
          const v = s.medianServiceMinutes > 0 ? s.medianServiceMinutes : Math.round(Number(s.serviceMinutes || 0))
          return sum + v
        }, 0)
        const driveMin = stops.reduce((sum, s) => sum + Math.round(Number(s.driveTimeMinutes || 0)), 0)
        const totalMin = serviceMin + driveMin
        const actualTotalMin = actualServiceMin + driveMin
        return {
          date,
          dayName: getDayName(weekDates[idx]),
          stops: stops.length,
          serviceMin,
          actualServiceMin,
          driveMin,
          totalMin,
          actualTotalMin,
          otMin: Math.max(0, totalMin - DAILY_OT_THRESHOLD_MIN),
          actualOtMin: Math.max(0, actualTotalMin - DAILY_OT_THRESHOLD_MIN)
        }
      })

      const agg = days.reduce((a, d) => {
        a.weekServiceMin += d.serviceMin; a.weekDriveMin += d.driveMin; a.weekTotalMin += d.totalMin; a.weekOtMin += d.otMin
        a.weekActualServiceMin += d.actualServiceMin; a.weekActualTotalMin += d.actualTotalMin; a.weekActualOtMin += d.actualOtMin
        return a
      }, { weekServiceMin: 0, weekDriveMin: 0, weekTotalMin: 0, weekOtMin: 0, weekActualServiceMin: 0, weekActualTotalMin: 0, weekActualOtMin: 0 })
      setWeeklyCrewStats({ days, ...agg })
    } catch (_err) {
      setWeeklyCrewStats(null)
    }
  }, [focusCrewId, focusDate, selectedScenarioId, url])

  useEffect(() => {
    fetchWeeklyCrewStats()
  }, [fetchWeeklyCrewStats])

  useEffect(() => {
    setWeeklyCrewStats(null)
  }, [focusCrewId])

  return {
    // Focused route
    focusedRoute,
    loadingFocusedRoute,
    fetchFocusedRoute,

    // Context dates
    contextDates,
    filteredContextDates,

    // Focus context stats
    focusContextStats,
    loadingFocusContextStats,
    fetchFocusContextStats,

    // Weekly crew stats
    weeklyCrewStats,
    fetchWeeklyCrewStats
  }
}
